import { coreTemplates } from "../core_templates.js";
import { findUncontainedSpawners } from "../templateGeometry.js";

// Helpers for assembling "maps" — named bundles of templates grouped into the
// five difficulty tiers. A map is plain data; the LevelGenerator consumes it.
//
// Map shape (see ./index.js for the registry):
//   {
//     id:          string   // URL-safe slug, used by ?map=<id>
//     name:        string   // human label
//     description: string
//     type:        "procedural" | "designed"
//     tiers:       { level1..level5: { <templateName>: { layout, metadata } } }
//     difficulty:  null | { thresholds?, phaseOut? }  // progression override
//   }
//
// "procedural" maps feed the distance-driven difficulty mix and run forever.
// "designed" maps are hand-built, finite courses: instead of `tiers` they carry
// a `scenes` dict and an ordered `sequence` of scene names ending in a finish —
// see `defineDesignedMap` at the bottom of this file and the "Maps" section of
// docs/level-generation.md.

const TIER_KEYS = ["level1", "level2", "level3", "level4", "level5"];
const SCENE_WIDTH = 30; // grid cells wide  — must match LevelGenerator.sceneWidth
const SCENE_HEIGHT = 20; // grid cells high — must match LevelGenerator.sceneHeight

/**
 * Select a named subset of templates from a tier object.
 * Throws on an unknown key so a typo in a curated map fails loudly at load
 * time instead of silently dropping a layout.
 *
 * @param {Object} source - a template object (e.g. easyTemplates)
 * @param {string[]} keys - template names to pull
 * @returns {Object} a new object containing just those templates
 */
export function pick(source, keys) {
  const result = {};
  for (const key of keys) {
    if (!source[key]) {
      const available = Object.keys(source).join(", ");
      throw new Error(
        `pick(): template "${key}" not found. Available: [${available}]`
      );
    }
    result[key] = source[key];
  }
  return result;
}

/**
 * Validate that a template's layout is a SCENE_WIDTH x SCENE_HEIGHT grid.
 * Catches malformed hand-authored layouts (wrong row count / ragged rows)
 * at module load rather than as a confusing runtime offset later.
 */
function validateTemplate(mapId, tier, name, template, checkSpawners = true) {
  if (!template || typeof template.layout !== "string") {
    throw new Error(
      `Map "${mapId}" tier ${tier}: template "${name}" is missing a layout string`
    );
  }
  const rows = template.layout.trim().split("\n");
  // Templates are bottom-anchored and may be shorter than the play area: empty
  // top rows are omitted and extrapolated as empty at render time. So any height
  // from 1 to SCENE_HEIGHT rows is valid.
  if (rows.length < 1 || rows.length > SCENE_HEIGHT) {
    throw new Error(
      `Map "${mapId}" tier ${tier}: template "${name}" has ${rows.length} rows, expected 1..${SCENE_HEIGHT}`
    );
  }
  rows.forEach((row, i) => {
    if (row.length !== SCENE_WIDTH) {
      throw new Error(
        `Map "${mapId}" tier ${tier}: template "${name}" row ${i} is ${row.length} wide, expected ${SCENE_WIDTH}`
      );
    }
  });

  // Spawner containment: every projectile spawner must have a backstop block to
  // its left in the same row so its rounds die inside the scene (see the
  // "Spawner containment rule" in docs/level-generation.md). Skippable for maps
  // that validate containment holistically across scene seams (e.g. custom
  // continuous levels, where a backstop may sit in the previous 30-col chunk).
  if (!checkSpawners) return;
  const offenders = findUncontainedSpawners(rows);
  if (offenders.length > 0) {
    const where = offenders
      .map((o) => `row ${o.row}, col ${o.col}`)
      .join("; ");
    throw new Error(
      `Map "${mapId}" tier ${tier}: template "${name}" has a projectile spawner ` +
        `with no backstop block to its left (${where}); rounds would fly out of frame.`
    );
  }
}

/**
 * Build a procedural map definition. Merges the shared core templates
 * (flat_ground) into the level1 tier and validates every grid up front.
 *
 * @param {Object} def
 * @param {string} def.id
 * @param {string} def.name
 * @param {string} [def.description]
 * @param {Object} def.tiers - partial { level1..level5 } of template objects
 * @param {Object|null} [def.difficulty] - optional DifficultyManager override
 * @returns {Object} a frozen map definition
 */
export function defineProceduralMap({
  id,
  name,
  description = "",
  tiers,
  difficulty = null,
}) {
  const resolvedTiers = {
    // Core templates first so a map can still override flat_ground if it ever
    // wants a themed variant, but gets a working default for free otherwise.
    level1: { ...coreTemplates, ...(tiers.level1 || {}) },
    level2: { ...(tiers.level2 || {}) },
    level3: { ...(tiers.level3 || {}) },
    level4: { ...(tiers.level4 || {}) },
    level5: { ...(tiers.level5 || {}) },
  };

  for (const tier of TIER_KEYS) {
    for (const [name, template] of Object.entries(resolvedTiers[tier])) {
      validateTemplate(id, tier, name, template);
    }
  }

  return Object.freeze({
    id,
    name,
    description,
    type: "procedural",
    tiers: resolvedTiers,
    difficulty,
  });
}

// --- Designed (finite, hand-built) maps --------------------------------------
//
// A designed map is a fixed course rather than an endless mix. It provides a
// `scenes` dict (same template shape as a tier) and an ordered `sequence` of
// scene names; the generator plays them in order instead of sampling by
// difficulty. The LAST scene in the sequence is the finish — a finish-line
// marker is rendered at its end and crossing it completes the run (see
// LevelGenerator's designed branch and Game.win()).
//
//   defineDesignedMap({
//     id: "gauntlet",
//     name: "The Gauntlet",
//     scenes: { intro: {...}, firstGap: {...}, finish: {...} },
//     sequence: ["intro", "firstGap", ..., "finish"],
//   })

/**
 * Build a designed (finite, hand-built) course definition. Validates every
 * scene grid the same way as a procedural tier and checks that the sequence
 * only references scenes that exist. The shared core templates (flat_ground)
 * are merged in so authors can use them in a sequence without redefining them.
 *
 * @param {Object} def
 * @param {string} def.id
 * @param {string} def.name
 * @param {string} [def.description]
 * @param {Object} def.scenes - { <sceneName>: { layout, metadata } }
 * @param {string[]} def.sequence - ordered scene names; the last is the finish
 * @param {"chained"|"absolute"} [def.placement="chained"] - how the generator
 *   places scenes vertically. "chained" (default, built-in maps) auto-aligns
 *   each scene's entry floor to the running height. "absolute" renders every
 *   scene at its authored bottom-anchored position (dy=0) — used by custom
 *   continuous levels so a painted canvas reproduces exactly.
 * @param {boolean} [def.requireSpawnerBackstop=true] - enforce the per-scene
 *   spawner-containment rule. Custom continuous maps pass false (containment is
 *   validated across the whole continuous grid by the editor instead).
 * @returns {Object} a frozen map definition (type: "designed")
 */
export function defineDesignedMap({
  id,
  name,
  description = "",
  scenes,
  sequence,
  placement = "chained",
  requireSpawnerBackstop = true,
}) {
  // Core templates first so authors can drop flat_ground into a sequence (and
  // still override it with a themed variant if they ever want to).
  const resolvedScenes = { ...coreTemplates, ...(scenes || {}) };

  for (const [sceneName, template] of Object.entries(resolvedScenes)) {
    validateTemplate(id, "designed", sceneName, template, requireSpawnerBackstop);
  }

  if (!Array.isArray(sequence) || sequence.length < 1) {
    throw new Error(
      `Designed map "${id}": sequence must be a non-empty array of scene names`
    );
  }
  for (const sceneName of sequence) {
    if (!resolvedScenes[sceneName]) {
      const available = Object.keys(resolvedScenes).join(", ");
      throw new Error(
        `Designed map "${id}": sequence references unknown scene ` +
          `"${sceneName}". Available: [${available}]`
      );
    }
  }

  return Object.freeze({
    id,
    name,
    description,
    type: "designed",
    placement,
    scenes: resolvedScenes,
    sequence: [...sequence],
    // The terminal scene; the finish line is drawn at its right edge.
    finishSceneName: sequence[sequence.length - 1],
    // Shape-compat with code that reads map.difficulty (DifficultyManager
    // accepts null). Designed maps don't use the distance-driven progression.
    difficulty: null,
  });
}
